import {
  GLYPH_SIZE,
  arrowHeadPath,
  base64,
  categoryOf,
  drawingPath,
  drawingShown,
  exportDrawings,
  exportRegionColor,
  fillable,
  fogPath,
  fogged,
  imageTypeOf,
  javaIsBlank,
  mapCaption,
  mapGrid,
  pinColor,
  pinIcon,
  pinShown,
  placeTooltip,
  regionLabelPositions,
  regionShown,
  sortedPins,
  svgNumber as num,
  type ExportAtlas,
  type ExportMap,
  type MapDrawing,
  type ResolvedIcon,
} from '@realmery/realm-core';

/**
 * A map in the HTML export (docs/PARITY-STEP-4-PLAN.md section 11), as the desktop's
 * `MapExport.figure` and `placesList` draw it: the image with an SVG overlay in its own
 * pixels - grid, tinted regions, drawings, haloed names, teardrop pins and tokens with their
 * icons - each pin and region linking to its entry or nested map and showing its notes on
 * hover; then every place in a list. Built as DOM nodes, so names and notes are only ever
 * text; pictures are embedded only when their bytes are an image, with the type they show.
 */

export interface MapHrefs {
  entry(entryId: string): string | null;
  map(mapId: string): string | null;
}

const SVG = 'http://www.w3.org/2000/svg';
export const PIN_PATH = 'M0,0 C-2.5,-6 -13,-12 -13,-22 A13,13 0 1,1 13,-22 C13,-12 2.5,-6 0,0 Z';
export const TOKEN_RADIUS = 17;
export const FOG_COLOR = '#1B1C22';

/** The CSS every HTML rendering of a map needs (`MapExport.CSS`). */
export const MAP_CSS = `.map-figure { margin: 1rem 0 1.5rem; }
.map-frame { position: relative; line-height: 0; border: 1px solid #b9ab8f; }
.map-frame img { width: 100%; height: auto; display: block; }
.map-overlay { position: absolute; left: 0; top: 0; width: 100%; height: 100%; }
.map-overlay a { cursor: pointer; }
.map-overlay a:hover .map-pin path, .map-overlay a:hover polygon { stroke-width: 3; }
.map-pin-label, .map-region-label { paint-order: stroke; stroke: #ffffff; stroke-width: 3px; stroke-linejoin: round; fill: #1f1a2b; font-family: Georgia, serif; }
.map-pin-label { font-weight: bold; }
.map-drawing-text { paint-order: stroke; stroke: #fffaf0; stroke-width: 4px; stroke-linejoin: round; font-family: Georgia, serif; }
.map-region-label { font-style: italic; opacity: 0.85; }
.map-caption { font-style: italic; color: #666; margin-top: 0.4rem; }
ul.map-places { columns: 2; }
.map-place-notes { color: #666; font-size: 0.92em; }
.map-cards { display: flex; flex-wrap: wrap; gap: 1rem; }
.map-card { width: 240px; text-decoration: none; color: inherit; }
.map-card img { width: 240px; height: 150px; object-fit: cover; border: 1px solid #b9ab8f; }
`;

/** A data URI for picture bytes, typed by what they are; null when they aren't a picture. */
export function pictureUri(data: Uint8Array): string | null {
  const type = imageTypeOf(data);
  return type ? `data:${type};base64,${base64(data)}` : null;
}

/** `Color.darker()` twice, as `#rrggbb` (`darker`). */
export function darker(hex: string): string {
  const rgb = parseHex(hex);
  const d = (c: number) => Math.max(Math.trunc(Math.max(Math.trunc(c * 0.7), 0) * 0.7), 0);
  return `#${rgb.map((c) => d(c).toString(16).padStart(2, '0')).join('')}`;
}

/** A colour's red, green and blue; the neutral grey when it can't be read (`color`). */
export function parseHex(hex: string): [number, number, number] {
  const m = /^#([0-9a-fA-F]{6})/.exec(hex.length === 9 ? hex.slice(0, 7) : hex);
  const value = m ? parseInt(m[1]!, 16) : 0x6b6470;
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

const hrefFor = (hrefs: MapHrefs, entryId: string | null, mapId: string | null) => (entryId !== null ? hrefs.entry(entryId) : null) ?? (mapId !== null ? hrefs.map(mapId) : null);

/** `imageSrc` is where the picture is: the HTML export embeds it; the static website links its file. */
export function mapFigure(doc: Document, atlas: ExportAtlas, m: ExportMap, hrefs: MapHrefs, includeHidden: boolean, imageSrc: string | null = pictureUri(m.map.imageData)): HTMLElement {
  const figure = doc.createElement('div');
  figure.className = 'map-figure';
  const frame = doc.createElement('div');
  frame.className = 'map-frame';
  figure.append(frame);
  const img = doc.createElement('img');
  if (imageSrc) img.setAttribute('src', imageSrc);
  img.setAttribute('alt', m.map.name);
  frame.append(img, mapSvg(doc, atlas, m, hrefs, includeHidden));
  const caption = mapCaption(m);
  if (caption !== '') {
    const p = doc.createElement('p');
    p.className = 'map-caption';
    p.textContent = caption;
    figure.append(p);
  }
  return figure;
}

export function mapSvg(doc: Document, atlas: ExportAtlas, m: ExportMap, hrefs: MapHrefs, includeHidden: boolean): SVGSVGElement {
  const make = (parent: Element, tag: string, attrs: Record<string, string> = {}, text?: string): Element => {
    const e = doc.createElementNS(SVG, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
    if (text !== undefined) e.textContent = text;
    parent.append(e);
    return e;
  };
  const link = (parent: Element, href: string | null) => (href === null ? parent : make(parent, 'a', { href }));
  const svg = doc.createElementNS(SVG, 'svg');
  svg.setAttribute('class', 'map-overlay');
  svg.setAttribute('viewBox', `0 0 ${m.width} ${m.height}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  const segments = mapGrid(m);
  if (segments.length > 0) {
    const grid = m.map.grid;
    make(svg, 'path', {
      d: segments.map(([x1, y1, x2, y2]) => `M${num(x1)},${num(y1)}L${num(x2)},${num(y2)}`).join(''),
      fill: 'none',
      stroke: grid.colorHex,
      'stroke-opacity': num(grid.opacity),
      'stroke-width': '1',
      'vector-effect': 'non-scaling-stroke',
    });
  }
  const scale = Math.max(1, m.width / 1000);
  for (const region of m.regions) {
    if (!regionShown(m, region, includeHidden)) continue;
    const color = exportRegionColor(atlas, region);
    const polygon = make(link(svg, hrefFor(hrefs, region.linkedEntryId, region.linkedMapId)), 'polygon', {
      points: region.points.map((p) => `${num(p.x)},${num(p.y)}`).join(' '),
      fill: color,
      'fill-opacity': region.visible ? '0.22' : '0.12',
      stroke: color,
      'stroke-width': '2',
      'vector-effect': 'non-scaling-stroke',
      class: 'map-region',
    });
    if (!region.visible) polygon.setAttribute('stroke-dasharray', '6 4');
    make(polygon, 'title', {}, placeTooltip(region.label, region.notes, region.visible));
  }
  for (const drawing of exportDrawings(m, includeHidden)) if (drawingShown(m, drawing, includeHidden)) svgDrawing(svg, drawing, make);
  if (fogged(m, includeHidden)) {
    make(svg, 'path', { d: fogPath(true, m.revealed, m.width, m.height), fill: FOG_COLOR, 'fill-rule': 'evenodd', class: 'map-fog' });
  }
  for (const [region, c] of regionLabelPositions(m, includeHidden, scale)) {
    make(svg, 'text', { x: num(c.x), y: num(c.y), 'font-size': num(18 * scale), 'text-anchor': 'middle', class: 'map-region-label' }, region.label);
  }
  for (const pin of sortedPins(m)) {
    if (!pinShown(m, pin, includeHidden)) continue;
    const group = make(link(svg, hrefFor(hrefs, pin.linkedEntryId, pin.linkedMapId)), 'g', { transform: `translate(${num(pin.x)},${num(pin.y)}) scale(${num(scale)})`, class: 'map-pin' });
    if (!pin.visible) group.setAttribute('opacity', '0.6');
    make(group, 'title', {}, placeTooltip(pin.label, pin.notes, pin.visible));
    const icon = pinIcon(atlas, pin);
    const color = pinColor(atlas, pin);
    const token = pin.style === 'TOKEN';
    const picture = icon?.kind === 'PICTURE';
    if (token) {
      const ring = make(group, 'circle', { r: num(TOKEN_RADIUS), fill: color, stroke: '#ffffff', 'stroke-width': '1.5' });
      if (!pin.visible) ring.setAttribute('stroke-dasharray', '3 2');
      if (!picture) make(group, 'circle', { r: num(TOKEN_RADIUS - 3), fill: darker(color) });
      svgIcon(group, icon, 0, 0, picture ? (TOKEN_RADIUS - 3) * 2 : (TOKEN_RADIUS - 3) * 1.35, `tok-${pin.id}`, make);
    } else {
      const body = make(group, 'path', { d: PIN_PATH, fill: color, stroke: '#ffffff', 'stroke-width': '1.5' });
      if (!pin.visible) body.setAttribute('stroke-dasharray', '3 2');
      svgIcon(group, icon, 0, -22, picture ? 23 : 16, `pin-${pin.id}`, make);
    }
    if (!javaIsBlank(pin.label)) {
      make(group, 'text', { x: '0', y: token ? num(TOKEN_RADIUS + 12) : '16', 'font-size': '12', 'text-anchor': 'middle', class: 'map-pin-label' }, pin.label);
    }
  }
  return svg;
}

type Make = (parent: Element, tag: string, attrs?: Record<string, string>, text?: string) => Element;

/** A drawing in the overlay: a stroked path (and arrowhead), or text with a halo (`svgDrawing`). */
function svgDrawing(svg: Element, drawing: MapDrawing, make: Make): void {
  if (drawing.kind === 'TEXT') {
    const at = drawing.points[0] ?? { x: 0, y: 0 };
    make(svg, 'text', { x: num(at.x), y: num(at.y + drawing.textSize * 0.8), 'font-size': num(drawing.textSize), 'font-weight': 'bold', fill: drawing.strokeColor, class: 'map-drawing-text' }, drawing.text);
    return;
  }
  const filled = fillable(drawing.kind) && drawing.fillColor !== null;
  const path = make(svg, 'path', {
    d: drawingPath(drawing),
    stroke: drawing.strokeColor,
    'stroke-width': num(drawing.strokeWidth),
    'stroke-linecap': drawing.dashed ? 'butt' : 'round',
    'stroke-linejoin': 'round',
    fill: filled ? drawing.fillColor! : 'none',
    class: 'map-drawing',
  });
  if (filled) path.setAttribute('fill-opacity', num(drawing.fillOpacity));
  if (drawing.dashed) path.setAttribute('stroke-dasharray', `${num(drawing.strokeWidth * 3)} ${num(drawing.strokeWidth * 2)}`);
  const head = arrowHeadPath(drawing);
  if (head !== '') {
    make(svg, 'path', { d: head, fill: drawing.strokeColor, stroke: drawing.strokeColor, 'stroke-linejoin': 'round', 'stroke-width': num(Math.max(1, drawing.strokeWidth / 3)) });
  }
}

/** A pin's icon, `size` across, centred on (cx, cy): a white glyph, a picture in a circle, an emoji, or a dot (`svgIcon`). */
function svgIcon(group: Element, icon: ResolvedIcon | null, cx: number, cy: number, size: number, clipId: string, make: Make): void {
  const dot = () => make(group, 'circle', { cx: num(cx), cy: num(cy), r: '4.5', fill: '#ffffff' });
  if (icon === null) {
    dot();
  } else if (icon.kind === 'GLYPH') {
    const path = make(group, 'path', { d: icon.path, fill: '#ffffff', transform: `translate(${num(cx - size / 2)},${num(cy - size / 2)}) scale(${num(size / GLYPH_SIZE)})` });
    if (icon.evenOdd) path.setAttribute('fill-rule', 'evenodd');
  } else if (icon.kind === 'PICTURE') {
    const href = pictureUri(icon.data);
    if (!href) {
      dot();
      return;
    }
    const id = clipId.replace(/[^A-Za-z0-9_-]/g, '');
    make(make(group, 'clipPath', { id }), 'circle', { cx: num(cx), cy: num(cy), r: num(size / 2) });
    make(group, 'image', { x: num(cx - size / 2), y: num(cy - size / 2), width: num(size), height: num(size), 'clip-path': `url(#${id})`, preserveAspectRatio: 'xMidYMid slice', href });
  } else {
    make(group, 'text', { x: num(cx), y: num(cy + 4.5), 'font-size': '13', 'text-anchor': 'middle', fill: '#ffffff' }, icon.text);
  }
}

/** Every shown pin and region, linked like the overlay (`placesList`). */
export function placesList(doc: Document, atlas: ExportAtlas, m: ExportMap, hrefs: MapHrefs, includeHidden: boolean): HTMLUListElement {
  const list = doc.createElement('ul');
  list.className = 'map-places';
  const item = (icon: string, label: string, categoryId: string | null, notes: string, entryId: string | null, mapId: string | null, visible: boolean) => {
    const li = doc.createElement('li');
    li.append(`${icon} `);
    const href = hrefFor(hrefs, entryId, mapId);
    const name = doc.createElement(href !== null ? 'a' : 'strong');
    name.textContent = javaIsBlank(label) ? '(unlabeled)' : label;
    if (href !== null) name.setAttribute('href', href);
    li.append(name);
    const category = categoryOf(atlas, categoryId);
    if (category) li.append(` - ${category.name}`);
    if (!visible) {
      const em = doc.createElement('em');
      em.textContent = ' (GM only)';
      li.append(em);
    }
    if (!javaIsBlank(notes)) {
      const span = doc.createElement('span');
      span.className = 'map-place-notes';
      span.textContent = notes;
      li.append(doc.createElement('br'), span);
    }
    list.append(li);
  };
  for (const pin of sortedPins(m)) {
    if (!pinShown(m, pin, includeHidden)) continue;
    const icon = pinIcon(atlas, pin);
    item(icon?.kind === 'TEXT' ? icon.text : '📍', pin.label, pin.categoryId, pin.notes, pin.linkedEntryId, pin.linkedMapId, pin.visible);
  }
  for (const region of m.regions) {
    if (regionShown(m, region, includeHidden)) item('⬠', region.label, region.categoryId, region.notes, region.linkedEntryId, region.linkedMapId, region.visible);
  }
  return list;
}

